import type { AdminPermission } from "../../contracts/admin-permissions";
import type {
  AdminRecognitionAuditDetail,
  AdminRecognitionComparisonMatrix,
  AdminRecognitionModelComparisonVariant,
  AdminRecognitionRawJsonComparison,
} from "../../contracts/admin-read-models";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import type { RecognitionAuditRawActionViewModel } from "./recognition-audit-view-model";

type RecognitionModelComparisonViewModelOptions = {
  locale?: AdminDashboardLocale;
  permissions: readonly AdminPermission[];
};

export type RecognitionModelComparisonCardViewModel = {
  rank: number;
  variantKey: string;
  provider: string;
  model: string;
  inputMode: string;
  statusLabel: string;
  validationStatusLabel: string;
  notExecutedReasonLabel?: string;
  isReturned: boolean;
  metrics: {
    latencyMs?: number;
    candidateCount?: number;
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    estimatedCostUsd?: number;
  };
  errorCode?: string;
  errorMessageRedacted?: string;
  normalizedResultPreview: Record<string, unknown>;
  rawResponseJson?: Record<string, unknown>;
  rawResponseStatus?: "available" | "unavailable" | "legacy_missing";
  rawAction: RecognitionAuditRawActionViewModel;
};

export type RecognitionModelComparisonViewModel = {
  groupStatusLabel: string;
  cards: RecognitionModelComparisonCardViewModel[];
  resultMatrix: AdminRecognitionComparisonMatrix;
  rawJsonComparison?: AdminRecognitionRawJsonComparison;
  rawVisibility: "visible" | "policy_hidden" | "not_available";
  legacyMessage?: string;
  singlePathUsage?: {
    provider: string;
    model: string;
    requestStatusLabel: string;
    rawAction: RecognitionAuditRawActionViewModel;
  };
};

function canAccessRaw(permissions: readonly AdminPermission[]): boolean {
  return permissions.includes("admin.recognition_audits.raw_access");
}

function toRawAction(
  rawResponseTargetId: string | undefined,
  permissions: readonly AdminPermission[],
): RecognitionAuditRawActionViewModel {
  return {
    targetKind: "gemini_response",
    targetId: rawResponseTargetId,
    state: !canAccessRaw(permissions) ? "hidden" : rawResponseTargetId ? "available" : "disabled",
  };
}

function localizeExecutionStatus(
  value: AdminRecognitionModelComparisonVariant["executionStatus"],
  locale: AdminDashboardLocale,
): string {
  const labels = {
    not_executed: { en: "Not executed", ko: "미실행" },
    queued: { en: "Queued", ko: "대기" },
    running: { en: "Running", ko: "실행 중" },
    succeeded: { en: "Succeeded", ko: "성공" },
    failed: { en: "Failed", ko: "실패" },
    cancelled: { en: "Cancelled", ko: "취소" },
  } as const;

  return pickAdminDashboardLocaleText(locale, labels[value]);
}

function localizeValidationStatus(
  value: AdminRecognitionModelComparisonVariant["validationStatus"],
  locale: AdminDashboardLocale,
): string {
  const labels = {
    not_run: { en: "Not run", ko: "미검증" },
    valid: { en: "Valid", ko: "유효" },
    schema_invalid: { en: "Schema invalid", ko: "스키마 오류" },
    json_invalid: { en: "JSON invalid", ko: "JSON 오류" },
    blocked: { en: "Blocked", ko: "차단됨" },
    empty: { en: "Empty", ko: "비어 있음" },
  } as const;

  return pickAdminDashboardLocaleText(locale, labels[value]);
}

function localizeNotExecutedReason(
  value: AdminRecognitionModelComparisonVariant["notExecutedReason"],
  locale: AdminDashboardLocale,
): string | undefined {
  if (!value) {
    return undefined;
  }

  const labels = {
    evaluation_disabled: { en: "Evaluation disabled", ko: "평가가 비활성화됨" },
    awaiting_continuation: { en: "Awaiting continuation", ko: "후속 실행 대기" },
    continuation_unavailable: { en: "Continuation unavailable", ko: "후속 실행 불가" },
    not_recorded: { en: "Not recorded", ko: "실행 결과 미기록" },
  } as const;

  return pickAdminDashboardLocaleText(locale, labels[value]);
}

function localizeGroupStatus(
  value: AdminRecognitionAuditDetail["modelComparison"]["groupStatus"],
  locale: AdminDashboardLocale,
): string {
  const labels = {
    not_created: { en: "Not created", ko: "생성되지 않음" },
    queued: { en: "Queued", ko: "대기" },
    running: { en: "Running", ko: "실행 중" },
    partial: { en: "Partial", ko: "부분 완료" },
    succeeded: { en: "Succeeded", ko: "성공" },
    failed: { en: "Failed", ko: "실패" },
    cancelled: { en: "Cancelled", ko: "취소" },
  } as const;

  return pickAdminDashboardLocaleText(locale, labels[value]);
}

function localizeRequestStatus(value: string, locale: AdminDashboardLocale): string {
  if (value === "succeeded") {
    return pickAdminDashboardLocaleText(locale, { en: "Succeeded", ko: "성공" });
  }
  if (value === "failed") {
    return pickAdminDashboardLocaleText(locale, { en: "Failed", ko: "실패" });
  }
  if (value === "cancelled") {
    return pickAdminDashboardLocaleText(locale, { en: "Cancelled", ko: "취소" });
  }
  if (value === "running") {
    return pickAdminDashboardLocaleText(locale, { en: "Running", ko: "실행 중" });
  }

  return value;
}

export function toRecognitionModelComparisonViewModel(
  detail: AdminRecognitionAuditDetail,
  options: RecognitionModelComparisonViewModelOptions,
): RecognitionModelComparisonViewModel {
  const locale = options.locale ?? "ko";
  const comparison = detail.modelComparison ?? {
    evaluationEnabled: false,
    groupStatus: "not_created" as const,
    variants: [],
    resultMatrix: { rows: [], differingRowCount: 0, matchingRowCount: 0 },
  };
  const rawAllowed = canAccessRaw(options.permissions);
  const cards = [...comparison.variants]
    .sort((left, right) => left.rank - right.rank)
    .map((variant) => {
      const rawResponseStatus = toRawResponseStatus(variant.rawResponseJson);
      return {
      rank: variant.rank,
      variantKey: variant.variantKey,
      provider: variant.provider,
      model: variant.model,
      inputMode: variant.inputMode,
      statusLabel: localizeExecutionStatus(variant.executionStatus, locale),
      validationStatusLabel: localizeValidationStatus(variant.validationStatus, locale),
      notExecutedReasonLabel: localizeNotExecutedReason(variant.notExecutedReason, locale),
      isReturned: comparison.returnedVariantKey === variant.variantKey,
      metrics: {
        latencyMs: variant.latencyMs,
        candidateCount: variant.candidateCount,
        inputTokens: variant.inputTokens,
        outputTokens: variant.outputTokens,
        totalTokens: variant.totalTokens,
        estimatedCostUsd: variant.estimatedCostUsd,
      },
      errorCode: variant.errorCode,
      errorMessageRedacted: variant.errorMessageRedacted,
      normalizedResultPreview: variant.normalizedResultPreview,
      rawResponseJson: rawAllowed && rawResponseStatus === "available" ? variant.rawResponseJson : undefined,
      rawResponseStatus,
      rawAction: toRawAction(variant.rawResponseTargetId, options.permissions),
      };
    });

  const hasRawJson = cards.some((card) => card.rawResponseJson !== undefined);

  return {
    groupStatusLabel: localizeGroupStatus(comparison.groupStatus, locale),
    cards,
    resultMatrix: rawAllowed
      ? comparison.resultMatrix ?? { rows: [], differingRowCount: 0, matchingRowCount: 0 }
      : { rows: [], differingRowCount: 0, matchingRowCount: 0 },
    rawJsonComparison: rawAllowed ? comparison.rawJsonComparison : undefined,
    rawVisibility: !rawAllowed
      ? "policy_hidden"
      : hasRawJson || comparison.rawJsonComparison !== undefined
        ? "visible"
        : "not_available",
    legacyMessage:
      cards.length === 0
        ? pickAdminDashboardLocaleText(locale, {
            en: "No comparison plan for this historical run",
            ko: "과거 실행에 비교 계획 없음",
          })
        : undefined,
    singlePathUsage: comparison.singlePathUsage
      ? {
          provider: comparison.singlePathUsage.provider,
          model: comparison.singlePathUsage.model,
          requestStatusLabel: localizeRequestStatus(comparison.singlePathUsage.requestStatus, locale),
          rawAction: toRawAction(comparison.singlePathUsage.rawResponseTargetId, options.permissions),
        }
      : undefined,
  };
}

function toRawResponseStatus(value: Record<string, unknown> | undefined): RecognitionModelComparisonCardViewModel["rawResponseStatus"] {
  if (!value) return undefined;
  if (value.rawResponseStatus === "unavailable") return "unavailable";
  if (value.rawResponseStatus === "legacy_missing") return "legacy_missing";
  return "available";
}
